import { registerBlock } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

// ─── Varicocele ─────────────────────────────────────────────────────

const varicocele: ContentBlock<"condition"> = {
  id: makeId("dx-varicocele"),
  kind: "condition",
  tags: ["his", "semen-analysis", "evidence-based"],
  title: "Varicocele",
  subtitle: "Enlarged veins in the scrotum affecting sperm production",
  body: {
    conditionName: "Varicocele",
    category: "male-factor",
    description:
      "A varicocele is an enlargement of the veins within the scrotum (pampiniform venous plexus), similar to varicose veins in the legs. The enlarged veins raise testicular temperature and create oxidative stress, which can impair sperm production, motility, and DNA integrity. Varicoceles are the most common identifiable cause of male infertility and, importantly, the most correctable.",
    symptoms: [
      "Often asymptomatic — found during fertility evaluation",
      "Dull ache or heaviness in the scrotum (worse with standing or activity)",
      "Visible or palpable enlarged veins ('bag of worms' appearance)",
      "Testicular atrophy (smaller testicle) on affected side",
      "Abnormal semen analysis parameters",
    ],
    howDiagnosed:
      "Physical examination by a urologist is the primary method — varicoceles are graded I-III based on palpability. Scrotal ultrasound with Doppler confirms diagnosis and measures vein diameter. Semen analysis assesses the functional impact. Usually left-sided (85%) due to venous anatomy.",
    impactOnFertility:
      "Varicoceles are found in approximately 35-40% of men with primary infertility and up to 80% with secondary infertility. They cause progressive decline in sperm parameters over time. However, not all varicoceles cause infertility — many men with varicoceles have normal fertility.",
    prevalence:
      "Present in approximately 15-20% of all men and 35-40% of men with infertility.",
    treatmentOptions: [
      "Microsurgical varicocelectomy (gold standard surgical repair)",
      "Percutaneous embolization (minimally invasive alternative)",
      "Laparoscopic varicocelectomy",
      "Antioxidant supplements while awaiting surgery or as adjunct",
      "IUI or IVF/ICSI if surgical repair doesn't improve parameters sufficiently",
    ],
    prognosis:
      "After varicocelectomy, semen parameters improve in 60-80% of men within 3-6 months. Spontaneous pregnancy rates improve by 30-40% after repair. When combined with ART, outcomes are even better. Improvement may take 3-6 months (one full spermatogenesis cycle).",
    forWhom: "him",
  },

  sources: [
    {
      title:
        "Diagnosis and Treatment of Infertility in Men: AUA/ASRM Guideline",
      authors: "Schlegel PN, Sigman M, Collura B, et al.",
      journal: "Journal of Urology",
      year: 2020,
      url: "https://www.asrm.org/practice-guidance/practice-committee-documents/diagnosis-and-treatment-of-infertility-in-men-auaasrm-guideline-part-i-2020/",
      evidenceLevel: "guideline",
    },
    {
      title: "EAU Guidelines on Sexual and Reproductive Health",
      authors:
        "European Association of Urology (Salonia A, Bettocchi C, Boeri L, et al.)",
      journal: "EAU",
      year: 2024,
      url: "https://uroweb.org/guidelines/sexual-and-reproductive-health",
      evidenceLevel: "guideline",
    },
  ],
};

registerBlock(varicocele);
